import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getUserApplication } from '../../../api/educentialApi';
import {
  ArrowLeft,
  CheckCircle,
  Clock,
  Circle,
  FileText,
  AlertCircle,
  Download,
  Upload,
  XCircle,
  Loader
} from 'lucide-react';

const OverseasApplicationTracker = () => {
  const { country = 'UK' } = useParams();
  const [loading, setLoading] = useState(true);
  const [application, setApplication] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadApplication();
  }, [country]);

  const loadApplication = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getUserApplication(country);
      setApplication(data);
    } catch (err) {
      console.error('Error loading application:', err);
      if (err.response?.status === 404) {
        setError('noApplication');
      } else {
        setError('Failed to load application. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Define 8-stage application workflow
  const getApplicationStages = () => {
    const status = application?.status || 'DRAFT';

    const stages = [
      {
        id: 1,
        title: 'Course Selection',
        description: 'Choose your program and university',
        status: status !== 'DRAFT' ? 'completed' : 'current'
      },
      {
        id: 2,
        title: 'Document Submission',
        description: 'Upload all required documents',
        status: ['DRAFT'].includes(status) ? 'pending' : ['SUBMITTED', 'UNDER_REVIEW', 'OFFER_SECURED', 'VISA_IN_PROGRESS', 'COMPLETED'].includes(status) ? 'completed' : 'current'
      },
      {
        id: 3,
        title: 'Application Review',
        description: 'Our team reviews your application',
        status: ['DRAFT', 'SUBMITTED'].includes(status) ? 'pending' : status === 'UNDER_REVIEW' ? 'current' : 'completed'
      },
      {
        id: 4,
        title: 'University Application',
        description: 'Application sent to universities',
        status: ['DRAFT', 'SUBMITTED', 'UNDER_REVIEW'].includes(status) ? 'pending' : ['OFFER_SECURED', 'VISA_IN_PROGRESS', 'COMPLETED'].includes(status) ? 'completed' : 'current'
      },
      {
        id: 5,
        title: 'Offer Received',
        description: 'University admission offer',
        status: ['DRAFT', 'SUBMITTED', 'UNDER_REVIEW'].includes(status) ? 'pending' : status === 'OFFER_SECURED' ? 'current' : ['VISA_IN_PROGRESS', 'COMPLETED'].includes(status) ? 'completed' : 'pending'
      },
      {
        id: 6,
        title: 'Payment & CAS',
        description: 'Tuition deposit and CAS issuance',
        status: ['DRAFT', 'SUBMITTED', 'UNDER_REVIEW', 'OFFER_SECURED'].includes(status) ? 'pending' : ['VISA_IN_PROGRESS', 'COMPLETED'].includes(status) ? 'completed' : 'pending'
      },
      {
        id: 7,
        title: 'Visa Application',
        description: 'Student visa processing',
        status: status === 'VISA_IN_PROGRESS' ? 'current' : status === 'COMPLETED' ? 'completed' : 'pending'
      },
      {
        id: 8,
        title: 'Pre-Departure',
        description: 'Travel and arrival preparation',
        status: status === 'COMPLETED' ? 'completed' : 'pending'
      }
    ];

    if (status === 'REJECTED') {
      return stages.map(s => ({ ...s, status: s.id <= 3 ? 'rejected' : 'pending' }));
    }

    return stages;
  };

  // Get status badge styling
  const getStatusBadge = (status) => {
    const badges = {
      DRAFT: { label: 'Draft', className: 'bg-gray-100 text-gray-700 border-gray-200' },
      SUBMITTED: { label: 'Submitted', className: 'bg-blue-100 text-blue-700 border-blue-200' },
      UNDER_REVIEW: { label: 'Under Review', className: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
      OFFER_SECURED: { label: 'Offer Secured', className: 'bg-green-100 text-green-700 border-green-200' },
      VISA_IN_PROGRESS: { label: 'Visa In Progress', className: 'bg-purple-100 text-purple-700 border-purple-200' },
      COMPLETED: { label: 'Completed', className: 'bg-green-100 text-green-700 border-green-200' },
      REJECTED: { label: 'Rejected', className: 'bg-red-100 text-red-700 border-red-200' }
    };

    const badge = badges[status] || badges.DRAFT;
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full border text-xs font-semibold ${badge.className}`}>
        {badge.label}
      </span>
    );
  };

  // Get document status icon
  const getDocumentStatusIcon = (hasDocument) => {
    if (hasDocument) {
      return <CheckCircle className="w-5 h-5 text-green-600" />;
    }
    return <XCircle className="w-5 h-5 text-gray-300" />;
  };

  // Check if document field has value
  const hasDocument = (section, field) => {
    return application?.[section]?.[field] && application[section][field] !== '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader className="w-12 h-12 text-yellow-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading application...</p>
        </div>
      </div>
    );
  }

  if (error === 'noApplication') {
    return (
      <div className="max-w-2xl mx-auto py-12 px-4">
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-8 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            No Application Found
          </h3>
          <p className="text-gray-600 mb-6">
            You haven't started a {country} study application yet. Begin your journey now!
          </p>
          <Link
            to="/dashboard/application"
            className="inline-block px-6 py-3 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg font-semibold transition-colors"
          >
            Start Application
          </Link>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto py-12 px-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-red-900 mb-2">Error</h3>
          <p className="text-red-800 mb-4">{error}</p>
          <button
            onClick={loadApplication}
            className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const stages = getApplicationStages();

  return (
    <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-8">
        <Link
          to="/services/overseas"
          className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Overseas Services
        </Link>

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Application Tracker
            </h1>
            <p className="text-gray-600">
              Track your {country} study abroad application progress
            </p>
          </div>
          {application && getStatusBadge(application.status)}
        </div>
      </div>

      {/* Application Reference */}
      {application?.applicationId && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
          <div className="flex items-center">
            <FileText className="w-5 h-5 text-blue-600 mr-3" />
            <div>
              <p className="text-sm font-semibold text-blue-900">Application Reference</p>
              <p className="text-lg font-mono font-bold text-blue-700">{application.applicationId}</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Progress Timeline */}
        <div className="lg:col-span-2">
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Application Progress</h2>

            <div className="space-y-4">
              {stages.map((stage, index) => {
                const isCompleted = stage.status === 'completed';
                const isCurrent = stage.status === 'current';
                const isRejected = stage.status === 'rejected';
                const isPending = stage.status === 'pending';

                return (
                  <div key={stage.id} className="relative">
                    {index !== stages.length - 1 && (
                      <div
                        className={`absolute left-6 top-12 bottom-0 w-0.5 ${
                          isCompleted ? 'bg-green-500' : 'bg-gray-200'
                        }`}
                        style={{ height: 'calc(100% + 1rem)' }}
                      />
                    )}

                    <div className={`flex items-start space-x-4 p-4 rounded-lg border-2 ${
                      isCompleted ? 'bg-green-50 border-green-200' :
                      isCurrent ? 'bg-yellow-50 border-yellow-400' :
                      isRejected ? 'bg-red-50 border-red-200' :
                      'bg-white border-gray-200'
                    }`}>
                      <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${
                        isCompleted ? 'bg-green-500 text-white' :
                        isCurrent ? 'bg-yellow-500 text-white' :
                        isRejected ? 'bg-red-500 text-white' :
                        'bg-gray-200 text-gray-500'
                      }`}>
                        {isCompleted ? <CheckCircle className="w-6 h-6" /> :
                         isCurrent ? <Clock className="w-6 h-6" /> :
                         isRejected ? <XCircle className="w-6 h-6" /> :
                         <Circle className="w-6 h-6" />}
                      </div>

                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                          {stage.title}
                        </h3>
                        <p className="text-sm text-gray-600">{stage.description}</p>

                        {isCurrent && (
                          <div className="mt-2 flex items-center text-sm text-yellow-700">
                            <Clock className="w-4 h-4 mr-1" />
                            Current step
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Document Checklist */}
        <div>
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 sticky top-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Document Checklist</h2>

            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Personal Documents</h3>
                <div className="space-y-2">
                  <DocumentCheckItem
                    label="Passport Bio Page"
                    hasDocument={hasDocument('personalInfo', 'passportBioPageFileId')}
                  />
                </div>
              </div>

              <div className="pt-4 border-t border-gray-200">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Academic Documents</h3>
                <div className="space-y-2">
                  <DocumentCheckItem
                    label="Degree Certificate"
                    hasDocument={hasDocument('academicDocuments', 'degreeCertificateFileId')}
                  />
                  <DocumentCheckItem
                    label="Transcripts"
                    hasDocument={hasDocument('academicDocuments', 'transcriptsFileId')}
                  />
                  <DocumentCheckItem
                    label="O'Level Result"
                    hasDocument={hasDocument('academicDocuments', 'waecNecoFileId')}
                  />
                  <DocumentCheckItem
                    label="Reference Letter"
                    hasDocument={hasDocument('academicDocuments', 'referenceLetterFileId')}
                  />
                  <DocumentCheckItem
                    label="Personal Statement"
                    hasDocument={hasDocument('academicDocuments', 'personalStatementFileId')}
                  />
                </div>
              </div>

              <div className="pt-4 border-t border-gray-200">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">English Proficiency</h3>
                <div className="space-y-2">
                  <DocumentCheckItem
                    label="IELTS/TOEFL"
                    hasDocument={hasDocument('englishProficiency', 'ieltsToeflPteFileId')}
                  />
                </div>
              </div>

              <div className="pt-4 border-t border-gray-200">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Visa Support</h3>
                <div className="space-y-2">
                  <DocumentCheckItem
                    label="Full Passport Pages"
                    hasDocument={hasDocument('visaSupport', 'passportFullPageFileId')}
                  />
                  <DocumentCheckItem
                    label="Proof of Funds"
                    hasDocument={hasDocument('visaSupport', 'proofOfFundsFileId')}
                  />
                </div>
              </div>

              {application && (
                <div className="pt-4 border-t border-gray-200">
                  <Link
                    to="/dashboard/application"
                    className="w-full flex items-center justify-center px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg font-semibold transition-colors text-sm"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Update Documents
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Next Steps */}
      {application && application.status !== 'COMPLETED' && application.status !== 'REJECTED' && (
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h2 className="text-lg font-bold text-blue-900 mb-3">Next Steps</h2>
          <ul className="space-y-2 text-sm text-blue-800">
            {application.status === 'DRAFT' && (
              <>
                <li>• Complete your application form</li>
                <li>• Upload all required documents</li>
                <li>• Submit your application for review</li>
              </>
            )}
            {application.status === 'SUBMITTED' && (
              <li>• Our team is reviewing your documents. You'll be notified once the review is complete.</li>
            )}
            {application.status === 'UNDER_REVIEW' && (
              <li>• Your application is being processed. We'll contact you if additional information is needed.</li>
            )}
            {application.status === 'OFFER_SECURED' && (
              <>
                <li>• Pay your tuition deposit</li>
                <li>• Await CAS (Confirmation of Acceptance for Studies)</li>
              </>
            )}
            {application.status === 'VISA_IN_PROGRESS' && (
              <>
                <li>• Complete your visa application</li>
                <li>• Attend visa interview (if required)</li>
                <li>• Track your visa status</li>
              </>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

// Document check item component
const DocumentCheckItem = ({ label, hasDocument }) => (
  <div className="flex items-center justify-between py-2">
    <span className="text-sm text-gray-700">{label}</span>
    {hasDocument ? (
      <CheckCircle className="w-5 h-5 text-green-600" />
    ) : (
      <Circle className="w-5 h-5 text-gray-300" />
    )}
  </div>
);

export default OverseasApplicationTracker;
